/*
 * etusivu-kysymys.js — the trigger for the iso kysymys band, and nothing else.
 *
 * docs/AGENT/editable-sections-standard.md §1 rule 4 is "native HTML first,
 * and no JavaScript", and it says of both existing exceptions that they "do
 * not travel" — a new band still starts at zero JavaScript and adding a script
 * means making the argument again. So here is the argument, and it is the
 * WEAKEST of the three in this theme. It should be read as such.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * THE ARGUMENT, INCLUDING THE PART AGAINST IT.
 *
 * Against: this is DECORATION. The blog's TOC scrollspy is navigational
 * feedback on a 2 000-word page and the rails' buttons fix a real defect (the
 * iOS edge-swipe back gesture). Nothing here is either. The band reads
 * perfectly with this file blocked, and no reader is worse off without it.
 * That is a materially weaker case than the two exceptions already granted and
 * it is recorded here so nobody cites this file as precedent for the next one.
 *
 * For: the band was built CSS-only first, with `animation-timeline: view()`,
 * and the client's three requests on 2026-09-21 are each individually
 * impossible under that mechanism rather than merely awkward:
 *
 *   1. "play out just once per reload" — a scroll-driven animation SCRUBS. It
 *      is a function of scroll position, so scrolling back up rewinds it and
 *      scrolling down re-runs it, forever. There is no once.
 *   2. "written out in a more natural flow, not each character with exact same
 *      pace" — under a scroll timeline the pace IS the reader's scroll wheel.
 *      Uneven per-character rhythm is meaningless when a flick of the wrist
 *      types the whole sentence.
 *   3. "run from start to finish once it is fully in viewport" — a scroll
 *      timeline cannot start a clock at a threshold; that is precisely the
 *      thing it does not do.
 *
 * CSS has no other lever. `@container scroll-state()` reports `stuck`,
 * `snapped` and `scrollable`, not "in viewport". `@starting-style` fires on
 * insertion, not on scroll. So the choice was between the client's three
 * requests and a 60-line file, not between a script and a CSS equivalent.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * WHAT IT DOES, and nothing else:
 *   0. picks which question to show, when the band carries more than one;
 *   1. works out whether the band has already passed its trigger point, and if
 *      so does nothing at all — a reader who loads the page already looking at
 *      the band sees the finished sentence, never a flash of empty line;
 *   2. otherwise writes `data-kysymys-js` on the section, which is the ONLY
 *      thing that hides the characters. Script blocked, script errored, module
 *      unsupported, IntersectionObserver missing: the attribute is absent and
 *      the sentence is simply there;
 *   3. writes `data-kysymys-kirjoita` once, when the band crosses its
 *      threshold, and disconnects. That attribute starts the animation. There
 *      is no second run and no rewind, which is the whole point.
 *
 * THE QUESTION CHOICE IS MADE HERE RATHER THAN IN LIQUID, and the reason is
 * the CDN. A storefront page is cached, so a server-side pick hands the same
 * "random" question to everyone who hits the same cached copy — and Liquid has
 * no random filter to make the pick with in the first place. Per-visitor
 * variation has to happen after the cache, which means the browser.
 *
 * It still creates no text. The section renders EVERY question, all but the
 * first shipped `hidden`; this file only MOVES which one is visible. Blocked,
 * errored or unsupported, the reader sees question one — exactly what they
 * would see if there were only one, which is the common case.
 *
 * `data-kysymys-js` does double duty as of 2026-09-21: it hides the
 * characters AND starts a caret blinking at the head of the line, so the band
 * announces itself as something about to be written rather than sitting empty
 * until the trigger. The caret is CSS; this file still writes no styles.
 *
 * ALL TIMING LIVES IN THE MARKUP, NOT HERE. Every character carries its own
 * `--d` (when it lands) and `--a` (how long the keystroke before it took),
 * computed in Liquid — see sections/etusivu-kysymys.liquid. This file knows
 * nothing about rhythm, duration or easing and must never learn: the cadence
 * is a design decision that belongs next to the copy it is timed against.
 *
 * ⚠️ THE 990px SCROLL-CONTAINER TRAP DOES APPLY HERE, unlike in
 * etusivu-kaista.js. Horizon puts `overflow: hidden` on html/body at ≥990px
 * and scrolls `.page-wrapper` instead, so a default-root IntersectionObserver
 * watches the wrong box on desktop. Hence the `@theme/scroll-container`
 * import, which is also what makes this a `type="module"`.
 *
 * ⚠️ AND SO DOES THE THEME-EDITOR TRAP. An inserted <script> never executes
 * and a module already in the module map does not re-evaluate, so a band whose
 * DOM Shopify replaces through the Section Rendering API goes dead until
 * reload — invisible on the storefront, hit repeatedly by the one person who
 * edits the page. `shopify:section:load` / `:unload` below, WeakMap-guarded
 * against double-binding.
 *
 * CONSTRAINTS, inherited from both existing exceptions and all binding:
 *   - it does nothing if its elements are absent;
 *   - everything reads correctly with the script blocked;
 *   - no library, no polyfill, no third-party request;
 *   - motion is opt-in (§6.1 item 9) — and it is CSS that enforces it here,
 *     not this file. Every rule that hides a character sits inside
 *     `@media (prefers-reduced-motion: no-preference)`, so a reader who asks
 *     for less motion gets the finished sentence even with the attributes
 *     present. That is deliberately stronger than a check in JavaScript: a
 *     media query is live, and the OS setting can change mid-session;
 *   - no customer-visible string is created here. Not one;
 *   - CLAUDE.md JS conventions: `globalThis` over `window`, `Number.*` over
 *     the globals, `element.remove()`.
 */

import { getIntersectionRoot, scrollContainerMediaQuery } from '@theme/scroll-container';

const doc = globalThis.document;

/* THE TRIGGER, in the client's words (2026-09-21): start "once the element
 * fills 60% of the viewport", so that a reader has the band well in view and
 * is already expecting something before the first character lands.
 *
 * ⚠️ TAKEN LITERALLY, THAT NEVER FIRES ON A PHONE, and the numbers are not
 * close: the band is ~631px in an 812px desktop viewport (78%), but only
 * ~312px in the same 812px on a phone — 38%, and 35% on a 430x932 handset. A
 * band shorter than 60% of the viewport can never fill 60% of it, so the
 * literal rule would leave the question permanently blank on mobile.
 *
 * So the bar is the SMALLER of "60% of the viewport" and "all of the band":
 *
 *     needed = min(0.6 x viewportHeight, bandHeight)
 *
 * On desktop that resolves to the client's rule. On a phone it collapses to
 * "the whole band is visible", which is exactly the mobile rule they asked
 * for in the previous round — so one expression now covers both and the
 * 750px breakpoint this file used to carry is gone. It also handles a band
 * TALLER than the viewport, where 60% is reached long before the band ends.
 */
const FILL_RATIO = 0.6;

/* Sub-pixel slack. Fractional layout (zoom, fluid clamps, a fractional
 * scroll position) makes an exact >= comparison here fail at some zoom
 * levels, and the failure mode is the animation silently never starting. */
const SLACK = 2;

/* Ratios are measured against the TARGET, but the rule above is about the
 * viewport, so no single threshold expresses it — a dense ladder is used and
 * the real test is done on the measured heights in the callback. This also
 * keeps the rule correct across a resize without rebuilding the observer. */
const THRESHOLDS = Array.from({ length: 21 }, (_, i) => i / 20);

class Kysymys {
  /** @param {Element} root the <section> */
  constructor(root) {
    this.root = root;
    this.observer = null;
    /* A resize changes both the viewport height the 60% is measured against
       and, above/below 990px, which element is the scroll root — so the
       observer is rebuilt rather than left measuring the old box. */
    this.onQueryChange = () => this.arm();
    scrollContainerMediaQuery?.addEventListener('change', this.onQueryChange);
    this.pickQuestion();
    this.arm();
  }

  /** Per-visitor choice among the rendered questions. Skipped in the theme
   *  editor, where the section deliberately renders every question unhidden so
   *  Susanna can select, scroll to and edit each one — a `hidden` block can do
   *  none of those. Runs BEFORE arm() because different questions wrap to
   *  different heights, and arm() measures the band. */
  pickQuestion() {
    if (globalThis.Shopify?.designMode) return;
    const options = this.root.querySelectorAll('[data-kysymys-vaihtoehto]');
    if (options.length < 2) return;
    const chosen = Math.floor(Math.random() * options.length);
    for (const [index, option] of options.entries()) {
      option.toggleAttribute('hidden', index !== chosen);
    }
  }

  /** The viewport rectangle the band is measured against — `.page-wrapper` at
   *  ≥990px, the real viewport below it. */
  rootRect() {
    const intersectionRoot = getIntersectionRoot();
    return intersectionRoot
      ? intersectionRoot.getBoundingClientRect()
      : { top: 0, bottom: globalThis.innerHeight, height: globalThis.innerHeight };
  }

  /** The one rule, shared by the mount-time check and the observer callback so
   *  the two can never disagree. See FILL_RATIO above. */
  static arrived(visibleHeight, bandHeight, rootHeight) {
    return visibleHeight >= Math.min(FILL_RATIO * rootHeight, bandHeight) - SLACK;
  }

  /** True if the band is ALREADY past its trigger point. Nothing is armed in
   *  that case: hiding a sentence the reader is looking at, purely so it can
   *  be typed back in a frame later, is a flash and not an animation. */
  alreadyArrived() {
    const rect = this.root.getBoundingClientRect();
    const view = this.rootRect();
    const visible = Math.max(0, Math.min(rect.bottom, view.bottom) - Math.max(rect.top, view.top));
    return Kysymys.arrived(visible, rect.height, view.height);
  }

  arm() {
    this.observer?.disconnect();
    this.observer = null;

    /* Already written, or written this session — never re-arm. "Once per
       reload" is enforced by this attribute, not by the observer. */
    if (this.root.hasAttribute('data-kysymys-kirjoita')) return;

    if (typeof globalThis.IntersectionObserver !== 'function' || this.alreadyArrived()) {
      this.root.removeAttribute('data-kysymys-js');
      return;
    }

    this.observer = new globalThis.IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const rootHeight = entry.rootBounds?.height ?? this.rootRect().height;
          if (!Kysymys.arrived(entry.intersectionRect.height, entry.boundingClientRect.height, rootHeight)) {
            continue;
          }
          this.root.setAttribute('data-kysymys-kirjoita', '');
          this.observer?.disconnect();
          this.observer = null;
          return;
        }
      },
      { root: getIntersectionRoot(), threshold: THRESHOLDS }
    );

    /* Written only now that a working observer exists — see item 2 in the
       header. This attribute is the single thing standing between a reader and
       an empty band, and it is also what starts the waiting caret blinking. */
    this.root.setAttribute('data-kysymys-js', '');
    this.observer.observe(this.root);
  }

  destroy() {
    this.observer?.disconnect();
    this.observer = null;
    scrollContainerMediaQuery?.removeEventListener('change', this.onQueryChange);
  }
}

/* WeakMap guard: `shopify:section:load` also fires for sections that were
   never torn down, and a second instance would arm a second observer. */
const instances = new WeakMap();

const roots = (scope) => [
  ...(scope.matches?.('[data-kysymys]') ? [scope] : []),
  ...scope.querySelectorAll('[data-kysymys]'),
];

const mount = (scope) => {
  for (const root of roots(scope)) {
    if (instances.has(root)) continue;
    instances.set(root, new Kysymys(root));
  }
};

const unmount = (scope) => {
  for (const root of roots(scope)) {
    instances.get(root)?.destroy();
    instances.delete(root);
  }
};

mount(doc);

/* Editor-only in practice — these events do not fire on a storefront visit. */
doc.addEventListener('shopify:section:load', (event) => mount(event.target));
doc.addEventListener('shopify:section:unload', (event) => unmount(event.target));
